import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { AdminLayout } from "./AdminLayout";

function ymd(d) { return d.toISOString().slice(0, 10); }

export default function AdminCalendar() {
  const [cursor, setCursor] = useState(new Date());
  const [artist, setArtist] = useState("");

  const { data: artists = [] } = useQuery({ queryKey: ["admin-artists"], queryFn: async () => (await api.get("/admin/artists")).data });
  const { data: bookings = [] } = useQuery({ queryKey: ["bookings-cal", artist], queryFn: async () => (await api.get("/admin/bookings", { params: { artist: artist || undefined } })).data });
  const artistMap = Object.fromEntries(artists.map((a) => [a.slug, a.name]));

  const byDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { if (b.preferred_date) { (m[b.preferred_date] = m[b.preferred_date] || []).push(b); } });
    return m;
  }, [bookings]);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  const today = ymd(new Date());

  return (
    <AdminLayout title="Calendar">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="border border-ash p-2 text-white hover:border-ember"><ChevronLeft size={16} /></button>
          <h2 className="font-serif text-3xl text-white w-56">{cursor.toLocaleString("default", { month: "long", year: "numeric" })}</h2>
          <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="border border-ash p-2 text-white hover:border-ember"><ChevronRight size={16} /></button>
        </div>
        <select value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="cal-artist-filter" className="bg-charcoal border border-ash text-white px-4 py-2.5 text-sm outline-none focus:border-ember">
          <option value="">All artists</option>
          {artists.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-7 border-l border-t border-ash" data-testid="admin-calendar">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="border-r border-b border-ash p-3 font-mono text-[0.6rem] uppercase tracking-widest text-[#828290] text-center">{d}</div>
        ))}
        {cells.map((date, i) => {
          const key = date ? ymd(date) : `e${i}`;
          const items = date ? byDate[key] || [] : [];
          return (
            <div key={key} className={`border-r border-b border-ash min-h-[100px] p-2 ${date && key === today ? "bg-ember/5" : ""}`}>
              {date && (
                <>
                  <div className={`font-mono text-xs mb-2 ${key === today ? "text-ember" : "text-[#828290]"}`}>{date.getDate()}</div>
                  <div className="space-y-1">
                    {items.slice(0, 3).map((b) => (
                      <div key={b.id} className="bg-elevated border-l-2 border-ember px-2 py-1 text-[0.65rem] text-white truncate" title={`${b.name} — ${artistMap[b.artist_slug] || "unassigned"}`}>
                        {b.name.split(" ")[0]} · {artistMap[b.artist_slug]?.split(" ")[0] || "TBD"}
                      </div>
                    ))}
                    {items.length > 3 && <div className="text-[0.6rem] text-[#828290]">+{items.length - 3} more</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
